import { useState } from "react";
import authService from "../../services/authService";
import { useLocation, useNavigate } from "react-router-dom";
import { validateEmail } from "../../helpers/validateEmail";
import SpinnerOverlay from "../SpinnerOverlay";
import AuthField from "../auth/AuthField";

const RegisterForm = () => {
    const location = useLocation();
    const [email, setEmail] = useState(location.state?.email || "");
    const [dni, setDni] = useState("");
    const [phone, setPhone] = useState("");
    const [name, setName] = useState("");
    const [lastName, setLastName] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!name.trim() || !lastName.trim()) {
            setError("Completá tu nombre y apellido.");
            document.getElementById(!name.trim() ? "name" : "lastName")?.focus();
            return;
        }

        if (password !== confirmPassword) {
            setError("Las contraseñas no coinciden.");
            return;
        }
        if (!validateEmail(email)) {
            setError("Email inválido.");
            return;
        }
        if (password && (password.length < 8 || new TextEncoder().encode(password).length > 72)) {
            setError("La contraseña debe tener al menos 8 caracteres y hasta 72 bytes.");
            return;
        }
        if (!/^\d+$/.test(dni) || dni.length < 7) {
            setError("El DNI debe contener solo números y al menos 7 dígitos.");
            return;
        }
        if (!/^\d+$/.test(phone)) {
            setError("El celular debe contener solo números.");
            return;
        }

        try {
            setLoading(true);
            const userData = { email, lastName, dni, phone, name, password };
            const user = await authService.register(userData);
            if (user) {
                navigate("/login", { state: { email } });
            }
        } catch (error) {
            setError(error.message || "No se pudo completar el registro.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <SpinnerOverlay isLoading={loading} />
            <form
                noValidate
                onSubmit={handleSubmit}
                className="space-y-4"
                autoComplete="on"
            >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <label
                            htmlFor="name"
                            className="mb-1 block text-sm font-medium text-zinc-300"
                        >
                            Nombre
                        </label>
                        <input
                            id="name"
                            type="text"
                            value={name}
                            placeholder="Nombre"
                            onChange={(e) => setName(e.target.value)}
                            className="block w-full rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-white outline-none transition focus:border-rose-500/70"
                            required
                            autoComplete="given-name"
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="lastName"
                            className="mb-1 block text-sm font-medium text-zinc-300"
                        >
                            Apellido
                        </label>
                        <input
                            id="lastName"
                            type="text"
                            value={lastName}
                            placeholder="Apellido"
                            onChange={(e) => setLastName(e.target.value)}
                            className="block w-full rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-white outline-none transition focus:border-rose-500/70"
                            required
                            autoComplete="family-name"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                        <label
                            htmlFor="dni"
                            className="mb-1 block text-sm font-medium text-zinc-300"
                        >
                            DNI
                        </label>
                        <input
                            id="dni"
                            type="text"
                            value={dni}
                            placeholder="Documento de identidad"
                            minLength={7}
                            onChange={(e) => setDni(e.target.value.replace(/\D/g, ""))}
                            className="block w-full rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-white outline-none transition focus:border-rose-500/70"
                            required
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="phone"
                            className="mb-1 block text-sm font-medium text-zinc-300"
                        >
                            Celular
                        </label>
                        <input
                            id="phone"
                            type="text"
                            value={phone}
                            placeholder="Número de celular"
                            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                            className="block w-full rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-white outline-none transition focus:border-rose-500/70"
                            required
                            autoComplete="tel"
                        />
                    </div>
                </div>

                <div>
                    <label
                        htmlFor="email"
                        className="mb-1 block text-sm font-medium text-zinc-300"
                    >
                        Email
                    </label>
                    <input
                        id="email"
                        type="email"
                        value={email}
                        placeholder="Email"
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2.5 text-white outline-none transition focus:border-rose-500/70"
                        required
                        autoComplete="email"
                    />
                </div>

                <p className="text-sm text-zinc-300">Podés dejar la contraseña vacía e ingresar con un código por email.</p>
                <AuthField id="password" label="Contraseña (opcional)" sensitive value={password} minLength={8}
                    disabled={loading} onChange={(e) => { setPassword(e.target.value); if (!e.target.value) setConfirmPassword(""); }} autoComplete="new-password"
                    help="Si elegís crear una, usá al menos 8 caracteres." />
                {password && <AuthField id="confirmPassword" label="Confirmar contraseña" sensitive value={confirmPassword}
                    disabled={loading} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" />}

                {error && (
                    <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                        {error}
                    </p>
                )}

                <button
                    type="submit"
                    className="w-full rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 px-5 py-3 font-medium text-white shadow-[0_10px_20px_rgba(244,63,94,0.25)] transition-transform hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={loading}
                >
                    {loading ? "Creando cuenta..." : "Crear cuenta"}
                </button>
            </form>
        </>
    );
};

export default RegisterForm;
